'use client'

import { useEffect, useState } from 'react'
import clsx from 'clsx'

import { Button, Target, MacIcon, WindowsIcon } from '@ccpgames/eve-web-core'
import { sendDownloadEvent } from '@/services/eve-web-api'
import { defaultDownloadButtonLabels } from './downloadButtonMicrocopy'
import type { DownloadButtonClientProps, DownloadPlatform } from './types'
import styles from './DownloadButton.module.css'

const DOWNLOAD_URLS = {
  win: 'https://launcher.ccpgames.com/eve-online/release/win32/x64/eve-online-latest+Setup.exe',
  mac: 'https://launcher.ccpgames.com/eve-online/release/darwin/universal/eve-online-latest-universal.dmg',
} as const

function detectPlatform(): DownloadPlatform {
  if (typeof navigator === 'undefined') return 'win'

  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: {
      platform?: string
    }
  }

  const detectedPlatform =
    navigatorWithUserAgentData.userAgentData?.platform ?? navigator.platform ?? navigator.userAgent

  return detectedPlatform.toLowerCase().includes('mac') ? 'mac' : 'win'
}

function getSecondaryPlatform(platform: DownloadPlatform): DownloadPlatform {
  return platform === 'win' ? 'mac' : 'win'
}

export function DownloadButtonClient({
  className,
  hideSecondaryPlatform = false,
  platform,
  primaryLabel = defaultDownloadButtonLabels.primaryLabel,
  downloadWindowsLabel = defaultDownloadButtonLabels.downloadWindowsLabel,
  downloadMacLabel = defaultDownloadButtonLabels.downloadMacLabel,
  onDownloadClick,
  small = false,
}: DownloadButtonClientProps) {
  const [activePlatform, setActivePlatform] = useState<DownloadPlatform>(platform ?? 'win')

  useEffect(() => {
    if (platform) {
      setActivePlatform(platform)
      return
    }

    setActivePlatform(detectPlatform())
  }, [platform])

  const secondaryPlatform = getSecondaryPlatform(activePlatform)
  const primaryHref = DOWNLOAD_URLS[activePlatform]
  const secondaryHref = DOWNLOAD_URLS[secondaryPlatform]
  const PrimaryIcon = activePlatform === 'win' ? WindowsIcon : MacIcon
  const secondaryLabel = secondaryPlatform === 'win' ? downloadWindowsLabel : downloadMacLabel

  function handleDownload(platformTarget: DownloadPlatform, href: string) {
    void sendDownloadEvent(platformTarget, href)
    onDownloadClick?.(platformTarget, href)
  }

  return (
    <div className={clsx(styles.downloadButtonContainer, className)}>
      <Target>
        <div
          className={clsx(styles.primaryButtonWrapper, small && styles.smallWrapper)}
          onClick={() => handleDownload(activePlatform, primaryHref)}
        >
          <Button
            component="a"
            href={primaryHref}
            className={clsx(styles.primaryButton, small && styles.small)}
            style={{
              width: '100%',
              padding: small ? '16px 34px' : '20px 50px',
              height: small ? '64px' : '80px',
            }}
          >
            <span className={styles.primaryButtonContent}>
              <span>{primaryLabel}</span>
              <PrimaryIcon aria-hidden className={styles.primaryIcon} width={28} height={28} />
            </span>
          </Button>
        </div>
      </Target>

      {!hideSecondaryPlatform && (
        <a
          className={styles.secondaryLink}
          href={secondaryHref}
          onClick={() => handleDownload(secondaryPlatform, secondaryHref)}
        >
          {secondaryLabel}
        </a>
      )}
    </div>
  )
}
